/**
 * KAN-897: строка "Мои лиды" (вынесено из MobileLeadsPage, KAN-834/851/857/864):
 * аватар, имя (ссылка на карточку растянута на строку), теплота, организация,
 * телефон. В "Отработано" бейдж исхода (Успех / Не успех).
 * KAN-900: кнопки "Отработано" в строке нет, итог ставится в карточке клиента.
 * KAN-915: дата и время следующей связи по поясу организации: во вкладке
 * "Следующая связь" запланированная, в "В работе" наступившая (пора связаться).
 * KAN-918: в "В работе" бейдж "Новое сообщение", если клиент написал после взятия.
 */
import { Link } from 'react-router-dom';
import { CalendarClock, ChevronRight, MessageCircle } from 'lucide-react';
import { ClientAvatar } from '@/components/clients/ClientAvatar';
import { LeadTemperatureBadge } from '@/components/leads/LeadTemperatureBadge';
import { MobileCompanyLink } from '@/components/mobile/MobileCompanyLink';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';
import { formatClientActivity, formatClientPhone } from '@/lib/clientListFormat';
import { mobileClientPath } from '@/lib/mobileApp';
import { clientTitle } from '@/lib/mobileClient';
import { mobileLeadOutcomeLabel, type MobileLeadsTab } from '@/lib/mobileLeadsTabs';
import { hasNewMessage, NEW_MESSAGE_LABEL } from '@/lib/newMessage';
import { formatZonedShort } from '@/lib/orgTimezone';
import { cn } from '@/lib/utils';
import type { Client } from '@/types/client';

interface MobileLeadRowProps {
  client: Client;
  /** Теплота для значка (у закрытого лида сохранённая). */
  level: string | null | undefined;
  /** KAN-915: вкладка списка (подпись даты связи). */
  tab?: MobileLeadsTab;
}

/** KAN-915: дата связи открытого лида: "Связь 23.10 12:30" или "Пора 19.10 10:00". */
function NextContact({ iso, tab }: { iso: string; tab: MobileLeadsTab }): JSX.Element {
  const timeZone = useOrgTimezone();
  const planned = tab === 'next';
  return (
    <span
      data-testid="mobile-lead-next-contact"
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        planned ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-800'
      )}
    >
      <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
      {planned ? 'Связь' : 'Пора'} {formatZonedShort(iso, timeZone)}
    </span>
  );
}

export function MobileLeadRow({ client, level, tab = 'work' }: MobileLeadRowProps): JSX.Element {
  const outcomeLabel = mobileLeadOutcomeLabel(client);
  const nextContact = tab !== 'worked' ? client.next_contact_at : null;
  const newMessage = tab === 'work' && hasNewMessage(client);
  return (
    <li data-testid="mobile-lead-row">
      <div className="relative flex min-h-[64px] items-center gap-3 px-4 py-3 active:bg-gray-50">
        {/* KAN-864: above the stretched card link (like MobileCompanyLink): the picture opens large. */}
        <span className="relative z-10 shrink-0">
          <ClientAvatar avatarUrl={client.avatar_url} name={clientTitle(client)} size={40} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <Link
              to={mobileClientPath(client.id)}
              className="truncate text-base font-medium text-gray-900 after:absolute after:inset-0 after:content-['']"
            >
              {clientTitle(client)}
            </Link>
            <span className="shrink-0 text-xs text-gray-500">{formatClientActivity(client.last_message_at)}</span>
          </div>
          <div className="flex min-w-0 items-center gap-2 text-sm text-gray-600">
            {newMessage && (
              <span
                data-testid="mobile-lead-new-message"
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700"
              >
                <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />
                {NEW_MESSAGE_LABEL}
              </span>
            )}
            {nextContact && <NextContact iso={nextContact} tab={tab} />}
            {outcomeLabel && (
              <span
                data-testid="mobile-lead-outcome"
                className={cn(
                  'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
                  client.outcome === 'won' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                )}
              >
                {outcomeLabel}
              </span>
            )}
            <LeadTemperatureBadge level={level} />
            <MobileCompanyLink company={client.client_company} fallback={client.company} className="truncate" />
            {client.phone && <span className="shrink-0">{formatClientPhone(client.phone)}</span>}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
      </div>
    </li>
  );
}
